/* eslint-disable @typescript-eslint/naming-convention */
import { Type } from '@sinclair/typebox';
import { surveyTableSchema } from '../domain/survey/survey/surveyTableSchema';
import { userTableSchema } from '../domain/user/userTableSchema';
import { createPathGuidParameter, createPathStringParameter } from './createPathParameter';
import { notFoundResponse, removedResponse } from './defaultResponses';

const objectGetSchema = Type.Object({
    id: Type.String(),
    name: Type.String(),
    code: Type.String(),
    clientCompanyId: Type.String(),
    ownerCompanyId: Type.String(),
    operatorCompanyId: Type.String(),
    surveyorCompanyId: Type.String(),
    objectTypeId: Type.String(),
    compositionIsVisible: Type.Boolean(),
    location: Type.String(),
    latitude: Type.Number(),
    longitude: Type.Number(),
    inspectionStandardId: Type.String(),
    isPublic: Type.Boolean(),
    status: Type.String(),
    usage: Type.String(),
    constructionYear: Type.Integer(),
    externalRefId: Type.String(),
    created_at: Type.String(),
    length: Type.Integer(),
    width: Type.Integer(),
    squareMeters: Type.String(),
    trafficType: Type.String(),
    mainMaterial: Type.String(),
    marineInfrastrutureType: Type.String(),
    managementOrganization: Type.String(),
    complexName: Type.String(),
    complexCode: Type.String(),
    complexLocation: Type.String(),
    municipality: Type.String(),
    province: Type.String(),
    attributes: Type.Any(),
    zxyExchangeAccessType: Type.String(),
    zxyExchangeAccessRegistrationId: Type.String(),
    labels: Type.Array(Type.String()),
    clientName: Type.String(),
    ownerName: Type.String(),
    surveyorName: Type.String(),
    operatorName: Type.String(),
    inspectionStandardType: Type.String(),
    objectTypeName: Type.String(),
    surveyId: Type.String(),
    surveryedOn: Type.String(),
    surveyCondition: Type.String(),
    shapeGeoJson: Type.Any(),
    canEdit: Type.Boolean(),
    canBatchAddSurvey: Type.Boolean(),
    objectTypeFieldsCount: Type.Integer(),
    remarks: Type.String(),
});

const sessionPostSchema = Type.Intersect([
    Type.Pick(userTableSchema, ['emailAddress']),
    Type.Object({ password: Type.String(), otpToken: Type.String() }),
]);

export const legacyPaths = {
    '/api/env': { get: { operationId: 'env.getEnvObject' } },
    '/api/session': {
        get: { operationId: 'session.getOne' },
        post: {
            operationId: 'session.create',
            requestBody: { content: { 'application/json': { schema: sessionPostSchema } } },
        },
        delete: { operationId: 'session.logout.delete' },
    },
    '/api/sessionSwitch': { post: { operationId: 'session.createForSelectedUser' } },
    '/api/tempsession': { post: { operationId: 'session.createTemp' } },
    '/api/session/logout': { get: { operationId: 'session.logout' } },
    '/api/checkToken': { get: { operationId: 'session.checkToken' } },
    '/api/support': { post: { operationId: 'support.create' } },
    '/api/languageList': { get: { operationId: 'language.getList' } },
    '/api/userpassword': { post: { operationId: 'user.updatePassword' } },
    '/api/userlanguage': { post: { operationId: 'user.updateLanguage' } },
    '/api/personalData': { get: { operationId: 'user.getPersonalData' }, post: { operationId: 'user.updatePersonalData' } },
    '/api/password/sendResetPasswordLink': { post: { operationId: 'user.sendResetPasswordLink' } },
    '/api/password/setPassword': { post: { operationId: 'user.setPassword' } },
    '/api/password/validateToken/{token}': {
        parameters: [createPathGuidParameter('token')],
        get: { operationId: 'user.validateToken' },
    },
    '/api/users': { get: { operationId: 'user.getAll' }, post: { operationId: 'user.create' } },
    '/api/users/{id}': {
        parameters: [createPathGuidParameter('id')],
        delete: { operationId: 'user.remove' },
        get: { operationId: 'user.getOne' },
        post: { operationId: 'user.update' },
    },
    '/api/users/{id}/addCompany': {
        parameters: [createPathGuidParameter('id')],
        post: { operationId: 'user.addToCompany' },
    },
    '/api/users/{id}/removeCompany': {
        parameters: [createPathGuidParameter('id')],
        post: { operationId: 'user.removeCompany' },
    },
    '/api/users/{id}/reset': {
        parameters: [createPathGuidParameter('id')],
        post: { operationId: 'user.resetPasswordUser' },
    },
    '/api/users/{id}/otpreset': {
        parameters: [createPathGuidParameter('id')],
        post: { operationId: 'user.removeOtpUser' },
    },
    '/api/user/company': { get: { operationId: 'companies.getOneForCurrentUser' } },
    '/api/companies': { get: { operationId: 'companies.getAll' }, post: { operationId: 'companies.create' } },
    '/api/companyList': { get: { operationId: 'companies.getCompanyList' } },
    '/api/companies/{id}': {
        parameters: [createPathGuidParameter('id')],
        delete: { operationId: 'companies.remove' },
        get: { operationId: 'companies.getOne' },
        post: { operationId: 'companies.update' },
    },
    '/api/companies/{id}/droneOperators': {
        parameters: [createPathGuidParameter('id')],
        get: { operationId: 'user.getDroneOperators' },
    },
    '/api/clients': { get: { operationId: 'companies.getClients' } },
    '/api/companyRelations': { get: { operationId: 'companyRelations.getAll' }, post: { operationId: 'companyRelations.create' } },
    '/api/companyRelations/{companyId}': {
        parameters: [createPathGuidParameter('companyId')],
        delete: { operationId: 'companyRelations.remove' },
    },
    '/api/companyUsages': { get: { operationId: 'companies.getMonthly' } },
    '/api/clientCoordinates': { get: { operationId: 'companies.getClientCoordinates' } },
    '/api/objects': { get: { operationId: 'objects.getAll' }, post: { operationId: 'objects.create' } },
    '/api/objectmarkers': { get: { operationId: 'objects.getAllMarkers' } },
    '/api/objectsComplexList': { get: { operationId: 'objects.getObjectComplexLocations' } },
    '/api/objects/import': { post: { operationId: 'objects.importObject' } },
    '/api/objects/knownLabels': { get: { operationId: 'objects.getKnownLabels' } },
    '/api/objects/{objectId}/importsurvey': {
        parameters: [createPathGuidParameter('objectId')],
        post: { operationId: 'objects.importSurvey' },
    },
    '/api/objects/{objectId}': {
        parameters: [createPathGuidParameter('objectId')],
        delete: {
            operationId: 'objects.remove',
            responses: {
                ...removedResponse,
                ...notFoundResponse,
            },
        },
        get: {
            operationId: 'objects.getOne',
            responses: {
                '200': {
                    description: 'Object data',
                    content: {
                        'application/json': {
                            schema: objectGetSchema,
                        },
                    },
                },
                ...notFoundResponse,
            },
        },
        post: { operationId: 'objects.update' },
    },
    '/api/objects/general/{id}': {
        parameters: [createPathGuidParameter('id')],
        post: { operationId: 'objects.updateGeneralData' },
    },
    '/api/objects/{objectId}/export': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'objects.exportObject' },
    },
    '/api/objects/{objectId}/exportComposition': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'objects.exportComposition' },
    },
    '/api/objects/{objectId}/shapeFile': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'objects.downloadShapeFile' },
    },
    '/api/objectTypesAvailable': { get: { operationId: 'objects.getAvailableObjectTypes' } },
    '/api/objects/{objectId}/elements': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'objects.getElements' },
        post: { operationId: 'objects.createElement' },
    },
    '/api/objects/{objectId}/categories': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'objects.getCategories' },
    },
    '/api/objects/{objectId}/elements/{elementId}': {
        parameters: [createPathGuidParameter('objectId'), createPathGuidParameter('elementId')],
        post: { operationId: 'objects.updateElement' },
        delete: { operationId: 'objects.removeElement' },
    },
    '/api/objects/{objectId}/units': {
        parameters: [createPathGuidParameter('objectId')],
        post: { operationId: 'objects.createUnit' },
    },
    '/api/objects/{objectId}/units/{unitId}/manifestations/bulk': {
        parameters: [createPathGuidParameter('objectId'), createPathGuidParameter('unitId')],
        post: { operationId: 'objects.bulkCreateManifestations' },
    },
    '/api/objects/{objectId}/units/{unitId}': {
        parameters: [createPathGuidParameter('objectId'), createPathGuidParameter('unitId')],
        post: { operationId: 'objects.updateUnit' },
        delete: { operationId: 'objects.removeUnit' },
    },
    '/api/objects/{objectId}/manifestations/{manifestationId}': {
        parameters: [createPathGuidParameter('objectId'), createPathGuidParameter('manifestationId')],
        delete: { operationId: 'objects.removeManifestation' },
        post: { operationId: 'objects.updateManifestation' },
    },
    '/api/objects/{objectId}/surveys': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'objects.surveys' },
    },
    '/api/objects/{objectId}/batches': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'batches.getBatchesForObject' },
    },
    '/api/objects/{objectId}/surveys/labels': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'survey.getObjectSurveysLabels' },
    },
    '/api/surveys': {
        get: {
            operationId: 'survey.getAll',
            parameters: [
                { name: 'limit', in: 'query', schema: Type.String(), required: true },
                { name: 'offset', in: 'query', schema: Type.String(), required: true },
            ],
        },
        post: { operationId: 'survey.create' },
    },

    '/api/surveys/knownLabels': {
        get: {
            operationId: 'survey.getKnownLabels',
            responses: {
                '200': {
                    description: 'Survey labels',
                    content: {
                        'application/json': {
                            schema: Type.Array(Type.String()),
                        },
                    },
                },
            },
        },
    },
    '/api/surveys/{surveyId}': {
        parameters: [createPathGuidParameter('surveyId')],
        delete: { operationId: 'survey.remove' },
        get: {
            operationId: 'survey.getOne',
            responses: {
                '200': {
                    description: 'Survey data',
                    content: {
                        'application/json': {
                            schema: Type.Pick(surveyTableSchema, ['status', 'assignedToUserId', 'reportDeadlineOnDate', 'linkToDocuments']),
                        },
                    },
                },
            },
        },
        post: {
            operationId: 'survey.update',
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Pick(surveyTableSchema, ['reportDeadlineOnDate', 'linkToDocuments']),
                    },
                },
            },
        },
    },
    '/api/surveys/{surveyId}/inspectionStandardData': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'survey.getInspectionStandardData' },
        post: { operationId: 'survey.updateInspectionStandardData' },
    },
    '/api/surveys/{surveyId}/sharelink': {
        parameters: [createPathGuidParameter('surveyId')],
        post: { operationId: 'survey.emailShareLink' },
    },
    '/api/surveys/{surveyId}/export': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'survey.exportSurvey' },
    },
    '/api/surveys/{surveyId}/flightlogs/from-assets': {
        parameters: [createPathGuidParameter('surveyId')],
        post: { operationId: 'handleResultRoutecreateFlightLogsFromVideos' },
    },
    '/api/survey/multipleImport': { post: { operationId: 'survey.importMultipleSurveys' } },
    '/api/surveys/{surveyId}/elements/{elementId}/remarks': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('elementId')],
        get: { operationId: 'survey.getElementRemarks' },
        post: { operationId: 'survey.createElementRemark' },
    },
    '/api/surveys/{surveyId}/units/{unitId}/remarks': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('unitId')],
        get: { operationId: 'survey.getUnitRemarks' },
        post: { operationId: 'survey.createUnitRemark' },
    },
    '/api/surveys/{surveyId}/manifestations/{manifestationId}/remarks': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('manifestationId')],
        get: { operationId: 'survey.getManifestationRemarks' },
        post: { operationId: 'survey.createManifestationRemark' },
    },
    '/api/surveys/{surveyId}/elements/{elementId}/condition': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('elementId')],
        get: { operationId: 'condition.getElementCondition' },
        post: { operationId: 'condition.createCondition.element' },
    },
    '/api/surveys/{surveyId}/elements/{elementId}/condition/{conditionId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('elementId'), createPathGuidParameter('conditionId')],
        post: { operationId: 'condition.updateCondition.element' },
    },
    '/api/surveys/{surveyId}/units/{unitId}/condition': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('unitId')],
        get: { operationId: 'condition.getUnitCondition' },
        post: { operationId: 'condition.createCondition.unit' },
    },
    '/api/surveys/{surveyId}/units/{unitId}/condition/{conditionId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('unitId'), createPathGuidParameter('conditionId')],
        post: { operationId: 'condition.updateCondition.unit' },
    },
    '/api/surveys/{surveyId}/manifestations/{manifestationId}/condition': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('manifestationId')],
        get: { operationId: 'condition.getManifestationCondition' },
        post: { operationId: 'condition.createCondition.manifestation' },
    },
    '/api/surveys/{surveyId}/manifestations/{manifestationId}/condition/{conditionId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('manifestationId'), createPathGuidParameter('conditionId')],
        post: { operationId: 'condition.updateCondition.manifestation' },
    },
    '/api/surveys/{surveyId}/condition/{conditionId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('conditionId')],
        delete: { operationId: 'condition.removeCondition' },
    },
    '/api/surveys/{surveyId}/recalculateDerivedCondition': {
        parameters: [createPathGuidParameter('surveyId')],
        post: { operationId: 'condition.recalculateDerivedConditionScoreReq' },
    },
    '/api/surveys/{surveyId}/resetAndCaculateConditons': {
        parameters: [createPathGuidParameter('surveyId')],
        post: { operationId: 'condition.resetAndRecalculateConditions' },
    },
    '/api/surveysize/{surveyId}': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'survey.getSize' },
    },
    '/api/surveys/{surveyId}/elementGroups/{elementGroupName}': {
        parameters: [createPathGuidParameter('surveyId'), createPathStringParameter('elementGroupName')],
        get: { operationId: 'survey.getSurveyElementGroup' },
        post: { operationId: 'survey.upsertSurveyElementGroup' },
    },
    '/api/surveys/{surveyId}/elements/{elementId}/condition/{conditionId}/defects': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('elementId'), createPathGuidParameter('conditionId')],
        get: { operationId: 'defectControllers.getDefects.element' },
        post: { operationId: 'defectControllers.create.element' },
    },
    '/api/surveys/{surveyId}/elements/{elementId}/defects': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('elementId')],
        post: { operationId: 'defectControllers.create.element.noCondition' },
    },
    '/api/surveys/{surveyId}/elements/{elementId}/condition/{conditionId}/defects/{defectId}': {
        parameters: [
            createPathGuidParameter('surveyId'),
            createPathGuidParameter('elementId'),
            createPathGuidParameter('conditionId'),
            createPathGuidParameter('defectId'),
        ],
        post: { operationId: 'defectControllers.update.element' },
        delete: { operationId: 'defectControllers.remove.element' },
    },
    '/api/surveys/{surveyId}/units/{unitId}/condition/{conditionId}/defects': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('unitId'), createPathGuidParameter('conditionId')],
        get: { operationId: 'defectControllers.getDefects.unit.condition' },
        post: { operationId: 'defectControllers.create.unit' },
    },
    '/api/surveys/{surveyId}/units/{unitId}/defects': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('unitId')],
        get: { operationId: 'defectControllers.getDefects.unit' },
        post: { operationId: 'defectControllers.create.unit.noCondition' },
    },
    '/api/surveys/{surveyId}/units/{unitId}/condition/{conditionId}/defects/{defectId}': {
        parameters: [
            createPathGuidParameter('surveyId'),
            createPathGuidParameter('unitId'),
            createPathGuidParameter('conditionId'),
            createPathGuidParameter('defectId'),
        ],
        post: { operationId: 'defectControllers.update.unit' },
        delete: { operationId: 'defectControllers.remove.unit' },
    },
    '/api/surveys/{surveyId}/manifestations/{manifestationId}/condition/{conditionId}/defects': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('manifestationId'), createPathGuidParameter('conditionId')],
        get: { operationId: 'defectControllers.getDefects.manifestation' },
        post: { operationId: 'defectControllers.create.manifestation' },
    },

    '/api/surveys/{surveyId}/manifestations/{manifestationId}/defects': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('manifestationId')],
        post: { operationId: 'defectControllers.create.manifestation.noCondition' },
    },
    '/api/surveys/{surveyId}/manifestations/{manifestationId}/condition/{conditionId}/defects/{defectId}': {
        parameters: [
            createPathGuidParameter('surveyId'),
            createPathGuidParameter('manifestationId'),
            createPathGuidParameter('conditionId'),
            createPathGuidParameter('defectId'),
        ],
        post: { operationId: 'defectControllers.update.manifestation' },
        delete: { operationId: 'defectControllers.remove.manifestation' },
    },
    '/api/nendefects': { get: { operationId: 'defectControllers.getNenDefects' } },
    '/api/surveys/{surveyId}/failureModes/': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'failureModeControllers.getFailureModes' },
        post: { operationId: 'failureModeControllers.create' },
    },
    '/api/surveys/{surveyId}/failureModes/{failureModeId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('failureModeId')],
        get: { operationId: 'failureModeControllers.getFailureMode' },
        delete: { operationId: 'failureModeControllers.remove' },
        post: { operationId: 'failureModeControllers.update' },
    },
    '/api/surveys/{surveyId}/failureModes/{failureModeId}/findings': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('failureModeId')],
        get: { operationId: 'findingControllers.getFindings' },
        post: { operationId: 'findingControllers.create' },
    },
    '/api/surveys/{surveyId}/failureModes/{failureModeId}/findings/{findingId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('failureModeId'), createPathGuidParameter('findingId')],
        get: { operationId: 'findingControllers.getFinding' },
        delete: { operationId: 'findingControllers.remove' },
        post: { operationId: 'findingControllers.update' },
    },
    '/api/surveys/{surveyId}/inspectionPlans/export': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'inspectionPlanControllers.exportInspectionPlans' },
    },
    '/api/surveys/{surveyId}/inspectionPlans/': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'inspectionPlanControllers.getInspectionPlans' },
        post: { operationId: 'inspectionPlanControllers.create' },
    },
    '/api/surveys/{surveyId}/inspectionPlans/{inspectionPlanId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('inspectionPlanId')],
        get: { operationId: 'inspectionPlanControllers.getInspectionPlan' },
        delete: { operationId: 'inspectionPlanControllers.remove' },
        post: { operationId: 'inspectionPlanControllers.update' },
    },
    '/api/surveys/{surveyId}/inspectionFindings/': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'inspectionFindingControllers.getInspectionFindings' },
        post: { operationId: 'inspectionFindingControllers.create' },
    },
    '/api/surveys/{surveyId}/inspectionFindings/{inspectionFindingId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('inspectionFindingId')],
        get: { operationId: 'inspectionFindingControllers.getInspectionFinding' },
        delete: { operationId: 'inspectionFindingControllers.remove' },
        post: { operationId: 'inspectionFindingControllers.update' },
    },
    '/api/maintenanceMeasures/': { get: { operationId: 'maintenanceMeasureControllers.getAllMaintenanceMeasures' } },
    '/api/surveys/{surveyId}/maintenanceMeasures': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'maintenanceMeasureControllers.getMaintenanceMeasures.survey' },
        post: { operationId: 'maintenanceMeasureControllers.create' },
    },
    '/api/objects/{objectId}/maintenanceMeasures': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'maintenanceMeasureControllers.getMaintenanceMeasures.object' },
    },
    '/api/surveys/{surveyId}/maintenanceMeasures/{maintenanceMeasureId}/current': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('maintenanceMeasureId')],
        get: { operationId: 'maintenanceMeasureControllers.getCurrentMaintenanceMeasure' },
    },
    '/api/surveys/{surveyId}/maintenanceMeasures/{maintenanceMeasureId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('maintenanceMeasureId')],
        get: { operationId: 'maintenanceMeasureControllers.getMaintenanceMeasure' },
        delete: { operationId: 'maintenanceMeasureControllers.remove' },
        post: { operationId: 'maintenanceMeasureControllers.update' },
    },
    '/api/surveys/{surveyId}/resetDefaultMaintenance': {
        parameters: [createPathGuidParameter('surveyId')],
        post: { operationId: 'maintenanceMeasureControllers.resetDefaultMaintenance' },
    },
    '/api/maintenancemeasures/{maintenanceMeasureId}': {
        parameters: [createPathGuidParameter('maintenanceMeasureId')],
        post: { operationId: 'maintenanceMeasureControllers.updateMaintenanceMeasureScheduledImplementationYear' },
    },
    '/api/surveys/statistics/progress': { get: { operationId: 'surveyStatistics.progressStatistics' } },
    '/api/objects/{objectId}/observationPoints': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'observationPoints.getAll.object' },
    },
    '/api/surveys/{surveyId}/observationPoints': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'observationPoints.getAll.survey' },
    },
    '/api/surveys/{surveyId}/observationPoints/{observationPointId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('observationPointId')],
        delete: { operationId: 'observationPoints.remove.survey' },
    },
    '/api/surveys/{surveyId}/observationPoints/': {
        parameters: [createPathGuidParameter('surveyId')],
        post: { operationId: 'observationPoints.create.survey' },
    },
    '/api/objects/{objectId}/observationPoints/': {
        parameters: [createPathGuidParameter('objectId')],
        post: { operationId: 'observationPoints.create.object' },
    },
    '/api/objects/{objectId}/observationPoints/{observationPointId}': {
        parameters: [createPathGuidParameter('objectId'), createPathGuidParameter('observationPointId')],
        delete: { operationId: 'observationPoints.remove.object' },
    },
    '/api/assets/still': { post: { operationId: 'assets.createStill' } },
    '/api/assets/fragment': { post: { operationId: 'assets.createFragment' } },
    '/api/assets': { post: { operationId: 'assets.create.all' }, get: { operationId: 'assets.getAll.all' } },
    '/api/assets/equipment': { post: { operationId: 'assets.create.equipment' } },
    '/api/assets/uploadurl': { get: { operationId: 'assets.getUploadUrl' } },
    '/api/assets/multipartUpload': { get: { operationId: 'assets.createMultipartUpload' } },
    '/api/assets/uploadPartUrl': { get: { operationId: 'assets.getUploadPartUrl' } },
    '/api/assets/completeMultipartUpload': { post: { operationId: 'assets.completeMultipartUpload' } },
    '/api/assets/order': { post: { operationId: 'assets.updateOrder' } },
    '/api/objects/{objectId}/assets/knownLabels': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'assets.getKnownLabels' },
    },
    '/api/objects/{objectId}/assets/{id}/download': {
        parameters: [createPathGuidParameter('objectId'), createPathGuidParameter('id')],
        get: { operationId: 'assets.getFile' },
    },
    '/api/objects/{objectId}/assets': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'assets.getAll.object' },
    },
    '/api/objects/{objectId}/assets/{assetId}': {
        parameters: [createPathGuidParameter('objectId'), createPathGuidParameter('assetId')],
        post: { operationId: 'assets.update.object' },
        delete: { operationId: 'assets.remove.object' },
    },
    '/api/assets/{id}': {
        parameters: [createPathGuidParameter('id')],
        get: { operationId: 'assets.getOne' },
    },
    '/api/assets/{id}/downscaleStatus': {
        parameters: [createPathGuidParameter('id')],
        get: { operationId: 'assets.getDownscaleStatus' },
    },
    '/api/assets/{id}/download': {
        parameters: [createPathGuidParameter('id')],
        get: { operationId: 'assets.getFile ' },
    },
    '/api/surveys/{surveyId}/assets/{assetId}': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('assetId')],
        delete: { operationId: 'assets.remove.survey' },
        post: { operationId: 'assets.update.survey' },
    },
    '/api/surveys/{surveyId}/assets/{assetId}/move': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('assetId')],
        post: { operationId: 'assets.moveAsset' },
    },
    '/api/surveys/{surveyId}/assets/{assetId}/copy': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('assetId')],
        post: { operationId: 'assets.copyAsset' },
    },
    '/api/removeAllAssets': { delete: { operationId: 'assets.removeAll' } },
    '/api/batches/{batchId}/assets': {
        parameters: [createPathGuidParameter('batchId')],
        get: { operationId: 'assets.getAll' },
    },
    '/api/batches/{batchId}/assets/{assetId}': {
        parameters: [createPathGuidParameter('batchId'), createPathGuidParameter('assetId')],
        delete: { operationId: 'assets.remove.batch' },
        post: { operationId: 'assets.update' },
    },
    '/api/equipmentAsset/{id}/download': {
        parameters: [createPathGuidParameter('id')],
        get: { operationId: 'equipmentAsset.getFile' },
    },
    '/api/equipment/{equipmentId}/history/export': {
        parameters: [createPathGuidParameter('equipmentId')],
        get: { operationId: 'exportHistory' },
    },
    '/equipment/flightLogExport': { get: { operationId: 'exportFlightLog' } },
    '/api/objectTypeElements': { post: { operationId: 'objectTypeElements.create' } },
    '/api/objectTypeElements/{id}': {
        parameters: [createPathGuidParameter('id')],
        post: { operationId: 'objectTypeElements.update' },
        delete: { operationId: 'objectTypeElements.remove' },
    },
    '/api/objectTypeUnits': { post: { operationId: 'objectTypeUnits.create' } },
    '/api/objectTypeUnits/{id}': {
        parameters: [createPathGuidParameter('id')],
        post: { operationId: 'objectTypeUnits.update' },
        delete: { operationId: 'objectTypeUnits.remove' },
    },
    '/api/objectTypeElementCodes': { get: { operationId: 'objectTypeElements.getObjectTypeElementCodes' } },
    '/api/objectTypeElementCodes/copy': { post: { operationId: 'objectTypeElements.copyObjectTypeElementCodes' } },
    '/api/objectTypeElementCodes/{id}': {
        parameters: [createPathGuidParameter('id')],
        post: { operationId: 'objectTypeElements.updateObjectTypeElementCode' },
    },
    '/api/objectTypeUnitCodes': { get: { operationId: 'objectTypeUnits.getObjectTypeUnitCodes' } },
    '/api/objectTypeUnitCodes/{id}': {
        parameters: [createPathGuidParameter('id')],
        post: { operationId: 'objectTypeUnits.updateObjectTypeUnitCode' },
    },
    '/api/defaultMaintenanceMeasures/{defaultMaintenanceMeasureId}': {
        parameters: [createPathGuidParameter('defaultMaintenanceMeasureId')],
        delete: { operationId: 'defaultMaintenanceMeasureControllers.remove' },
        post: { operationId: 'defaultMaintenanceMeasureControllers.update' },
    },
    '/api/defaultMaintenanceMeasures': {
        get: { operationId: 'defaultMaintenanceMeasureControllers.getMaintenanceMeasures' },
        post: { operationId: 'defaultMaintenanceMeasureControllers.create' },
    },
    '/api/defaultMaintenanceMeasures/copy/{clientCompanyId}': {
        parameters: [createPathGuidParameter('clientCompanyId')],
        post: { operationId: 'defaultMaintenanceMeasureControllers.copyDefaultMaintenance' },
    },
    '/api/defaultFailureModes': {
        get: { operationId: 'defaultFailureModeControllers.getDefaultFailureModes' },
        post: { operationId: 'defaultFailureModeControllers.create' },
    },
    '/api/defaultFailureModes/{defaultFailureModeId}': {
        parameters: [createPathGuidParameter('defaultFailureModeId')],
        delete: { operationId: 'defaultFailureModeControllers.remove' },
        post: { operationId: 'defaultFailureModeControllers.update' },
    },
    '/api/inspectionStandards': { get: { operationId: 'inspectionStandards.getAll' } },
    '/api/inspectionStandards/{id}/elements': {
        parameters: [createPathGuidParameter('id')],
        get: { operationId: 'inspectionStandards.getDecomposition' },
    },
    '/api/inspectionStandards/{id}/categories': {
        parameters: [createPathGuidParameter('id')],
        get: { operationId: 'inspectionStandards.getCategories' },
    },
    '/api/inspectionStandards/{id}/import': {
        parameters: [createPathGuidParameter('id')],
        post: { operationId: 'inspectionStandards.importStandard' },
    },
    '/api/batches': { get: { operationId: 'batches.getAll' }, post: { operationId: 'batches.create' } },
    '/api/batches/markers': { get: { operationId: 'batches.getAllMarkers' } },
    '/api/batcheslist': { get: { operationId: 'batches.getList' } },
    '/api/batches/{batchId}/executorswithsameobject': {
        parameters: [createPathGuidParameter('batchId')],
        get: { operationId: 'batches.getBatchExecutorsWithSameObject' },
    },
    '/api/batches/{batchId}': {
        parameters: [createPathGuidParameter('batchId')],
        get: { operationId: 'batches.getBatch' },
        post: { operationId: 'batches.update' },
        delete: { operationId: 'batches.remove' },
    },
    '/api/batches/{batchId}/objects': {
        parameters: [createPathGuidParameter('batchId')],
        get: { operationId: 'batches.getObjects' },
        post: { operationId: 'batches.addObject' },
    },
    '/api/batches/{batchId}/objects/list': {
        parameters: [createPathGuidParameter('batchId')],
        post: { operationId: 'batches.addObjectList' },
    },
    '/api/batches/{batchId}/batchObject': {
        parameters: [createPathGuidParameter('batchId')],
        delete: { operationId: 'batches.removeAllObjects' },
    },
    '/api/batches/{batchId}/object/{objectId}': {
        parameters: [createPathGuidParameter('batchId'), createPathGuidParameter('objectId')],
        delete: { operationId: 'batches.removeObject' },
    },
    '/api/generateReport/{surveyId}': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'report.downloadReport' },
    },
    '/api/surveys/{surveyId}/makePdf': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'pdfReport' },
    },
    '/api/surveys/{surveyId}/nenExcel': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'reportNen' },
    },
    '/api/surveys/{surveyId}/fmecaExcel': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'reportFmeca' },
    },
    '/api/surveys/{surveyId}/miokExcelSurvey': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'miokReportSurvey.survey' },
    },
    '/api/batches/{batchId}/miokExcel': {
        parameters: [createPathGuidParameter('batchId')],
        get: { operationId: 'miokReportSurvey.batch' },
    },
    '/api/batches/{batchId}/batchExcel': {
        parameters: [createPathGuidParameter('batchId')],
        get: { operationId: 'batchExcelExport' },
    },
    '/api/export/miokExcelSurvey': { get: { operationId: 'miokReportSurvey.all' } },
    '/api/export/miokExcelObject': { get: { operationId: 'miokReportObject.all' } },
    '/api/export/nenExcelObject': { get: { operationId: 'reportNenObjectsList' } },
    '/api/objects/{objectId}/miokExcel': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'miokReportObject.object' },
    },
    '/api/surveyInvestigations/excel': { get: { operationId: 'furtherInvestigationReport' } },
    '/api/surveys/{surveyId}/apiEemuaExcel': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'apiEemuaReport' },
    },
    '/api/wfs': { get: { operationId: 'wfs.handle.get' }, post: { operationId: 'wfs.handle.post' } },
    '/api/company/drones/licences/': { post: { operationId: 'droneController.useLicence' }, get: { operationId: 'droneController.getLicences' } },
    '/api/company/drones/licences/{id}': {
        parameters: [createPathGuidParameter('id')],
        get: { operationId: 'droneController.getLicence' },
    },
    '/api/company/drones/projects/': { post: { operationId: 'droneController.createProjects' } },
    '/api/company/authenticate/': { post: { operationId: 'droneController.getAuthenticationToken' } },
    '/api/companies/{companyId}/licences/': {
        parameters: [createPathGuidParameter('companyId')],
        get: { operationId: 'handleResultRoutelicenceController.getAll' },
        post: { operationId: 'handleResultRoutelicenceController.create' },
    },
    '/api/companies/{companyId}/licences/{licenceId}': {
        parameters: [createPathGuidParameter('companyId'), createPathGuidParameter('licenceId')],
        delete: { operationId: 'handleResultRoutelicenceController.remove' },
        get: { operationId: 'handleResultRoutelicenceController.getOne' },
        post: { operationId: 'handleResultRoutelicenceController.update' },
    },
    '/api/utprojects/{projectId}': {
        parameters: [createPathGuidParameter('projectId')],
        get: { operationId: 'handleResultRouteprojectController.getOne' },
        post: { operationId: 'handleResultRouteprojectController.update' },
    },
    '/api/utProjectsNominals/{projectId}': {
        parameters: [createPathGuidParameter('projectId')],
        post: { operationId: 'handleResultRouteprojectController.updateNominal' },
    },
    '/api/utprojects/{projectId}/probeMhz': {
        parameters: [createPathGuidParameter('projectId')],
        post: { operationId: 'handleResultRouteprojectController.updateProbeMhz' },
    },
    '/api/utprojects/{projectId}/export': {
        parameters: [createPathGuidParameter('projectId')],
        get: { operationId: 'handleResultRouteprojectController.exportFlights' },
    },
    '/api/utprojects/{targetProjectId}/merge': {
        parameters: [createPathGuidParameter('targetProjectId')],
        post: { operationId: 'handleResultRouteprojectController.merge' },
    },
    '/api/utprojects/{projectId}/measurements': {
        parameters: [createPathGuidParameter('projectId')],
        get: { operationId: 'handleResultRouteprojectController.getAllProjectMeasurements' },
        post: { operationId: 'handleResultRouteprojectController.createProjectMeasurement' },
    },
    '/api/utprojects/{projectId}/measurements/{measurementId}': {
        parameters: [createPathGuidParameter('projectId'), createPathGuidParameter('measurementId')],
        get: { operationId: 'handleResultRouteprojectController.getOneProjectMeasurement' },
    },
    '/api/surveys/{surveyId}/utprojects/importCsv': {
        parameters: [createPathGuidParameter('surveyId')],
        post: { operationId: 'projectController.importUtProjectCsv' },
    },
    '/api/surveys/{surveyId}/measurements': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'handleResultRouteprojectController.getAllSurveyMeasurements' },
    },
    '/api/surveys/{surveyId}/utprojects': {
        parameters: [createPathGuidParameter('surveyId')],
        get: { operationId: 'handleResultRouteprojectController.getAllForSurvey' },
    },
    '/api/utprojects/{projectId}/batch-flightlogs': {
        parameters: [createPathGuidParameter('projectId')],
        post: { operationId: 'handleResultRoutecreateFlightLogsFromProjectFlights' },
    },
    '/api/utprojects/download/excel': {
        get: { operationId: 'handleResultRouteprojectController.exportProjects' },
    },
    '/api/auditevents/export': { get: { operationId: 'handleResultRouteexportAuditEvents' } },
    '/api/zxyDataExchange/v1/objects': {
        get: { operationId: 'handleResultRoutezxyDataExchangeRecipient.getObjects' },
        post: { operationId: 'handleResultRoutezxyDataExchangeRecipient.createObject' },
    },
    '/api/zxyDataExchange/v1/objects/{objectId}': {
        parameters: [createPathGuidParameter('objectId')],
        get: { operationId: 'handleResultRoutezxyDataExchangeRecipient.getObject' },
    },
    '/api/hubSpotDeals': { get: { operationId: 'handleResultRoutehubSpot.getDeals' } },
    '/api/applicationstatus/health': { get: { operationId: 'applicationStatusControllers.checkHealth' } },
    '/api/applicationstatus/config': { get: { operationId: 'applicationStatusControllers.checkConfiguration' } },
} as const;
